import { ClickAwayListener, Grow, MenuItem, Paper, Popper } from "@mui/material";
import { isAxiosError } from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import US from "../../assets/images/flag/US.png";
import VN from "../../assets/images/flag/VN.png";
import ChevronDown10pxSvg from "../../assets/images/icons/ChevronDown10pxSvg";
import logo_light from "../../assets/images/logo/CheckIDBioSense_light.svg";
import { getAccountList } from "../../axios/Account/AccountApi";
import { getCompany } from "../../axios/Company/CompanyApi";
import {
  accessInviteEmail,
  detailInviteEmail,
  inviteDecode,
  activeDecode,
  rejectInviteEmail,
} from "../../axios/Invitation/InvitationApi";
import { getConfigsLogin } from "../../axios/Login/loginApi";
import {
  PrimaryButtonFullWidth,
  WhiteButtonFullWidth,
} from "../../components/StyledButton";
import { NavbarLanguageMenuList } from "../../components/StyledComponents";
import { SpinnerRing } from "../../components/CustomSpinner";
import { useAuth } from "../../context/AuthContext";
import { useLoading } from "../../context/LoadingContext";
import i18n from "../../n18i";
import { changeLanguage } from "../../redux/language/reducer";

const Invitation = () => {
  const { t, i18n: i18nHook } = useTranslation();
  const dispatch = useDispatch();
  const [show, hide] = useLoading();
  const params = useSearchParams();
  const navigate = useNavigate();
  const inviteCode = params[0].get("inviteCode");
  // .get() decode sẵn → encode lại để giữ đúng dạng trên URL (%2F, %2B, %3D)
  const activateCodeDecoded = params[0].get("activateCode");
  const activateCode = activateCodeDecoded
    ? encodeURIComponent(activateCodeDecoded)
    : null;
  const [listAccountCompany, setListAccountCompany] = useState([]);
  const [inviteDetailData, setInviteDetailData] = useState(null);
  const [inviteData, setInviteData] = useState({
    expiresIn: Date.now(),
    toUser: "",
    hasAccount: false,
    companyId: "",
    fromUser: "",
    role: "",
    inviteId: "",
    centizenNumber: ""
  });
  const [company, setCompany] = useState({ companyName: "" });
  const {
    isAuthenticated,
    isReady,
    isAdmin,
    profile,
    refreshProfile,
    setCookie,
    getCookie,
    accessToken,
    logout,
  } = useAuth();
  const anchorRefLanguage = useRef(null);
  const [openLanguage, setOpenLanguage] = useState(false);
  const activateRedirectedRef = useRef(false);
  const [activatePhase, setActivatePhase] = useState(
    activateCode ? "redirecting" : "idle"
  );

  const currentLang = i18nHook.language?.startsWith("vi") ? "vi" : "en";
  const currentFlag = currentLang === "vi" ? VN : US;

  const handleChangeLanguage = (language) => {
    i18n.changeLanguage(language);
    dispatch(changeLanguage(language));
    localStorage.setItem("i18nextLngId", language === "vi" ? "1" : "2");
    setOpenLanguage(false);
  };

  // Luồng kích hoạt: nếu đang đăng nhập → logout rồi quay lại link; chưa login → Keycloak SSO
  useEffect(() => {
    if (!activateCode || activateRedirectedRef.current) return;
    // Chờ auth bootstrap xong để biết đã có session hay chưa
    if (!isReady) return;

    activateRedirectedRef.current = true;

    const invitationReturnUrl = `${window.location.origin}/portal/invitation?activateCode=${activateCode}`;

    const redirectToKeycloak = async () => {
      try {
        localStorage.setItem("activateCode", activateCode);

        const { data: decodeData } = await activeDecode(activateCode);
        const citizenNumber = decodeData?.citizenNumber;
        console.log("[activeDecode] response.data:", decodeData);

        const hasSession =
          isAuthenticated ||
          Boolean(accessToken) ||
          Boolean(getCookie("accessToken"));

        // Đã đăng nhập → loading + toast, logout SSO rồi gọi lại đúng link activate
        if (hasSession) {
          sessionStorage.setItem("activateAccountLogout", "true");
        
          setActivatePhase("logging-out");
        
          await logout({
            postLogoutRedirectUri: invitationReturnUrl,
            silent: true,
          });
        
          return;
        }

        setActivatePhase("redirecting");
        const { data } = await getConfigsLogin();
        const redirectUri = `${window.location.origin}/portal/account/active`;

        const authUrl = new URL(
          `${data.authority}/protocol/openid-connect/auth`
        );
        authUrl.searchParams.set("client_id", data.client_id);
        authUrl.searchParams.set("response_type", "code");
        authUrl.searchParams.set("scope", "openid profile identity email");
        authUrl.searchParams.set("redirect_uri", redirectUri);
        authUrl.searchParams.set("state", activateCode);
        // Prefill CCCD trên màn SSO (readonly)
        authUrl.searchParams.set("gp_login_method", "cccd");
        authUrl.searchParams.set("gp_login_id", citizenNumber || "");
        authUrl.searchParams.set("gp_login_id_readonly", "1");

        window.location.href = authUrl.toString();
      } catch (error) {
        console.error("Activate account redirect error:", error);
        toast.error(t("ERRORMSG_0001"));
        navigate("/login", { replace: true });
      }
    };

    redirectToKeycloak();
  }, [
    activateCode,
    isReady,
    isAuthenticated,
    accessToken,
    getCookie,
    logout,
    t,
    navigate,
  ]);

  const isTokenExpires = useMemo(
    () => Date.now() > parseInt(inviteData.expiresIn),
    [inviteData.expiresIn]
  );

  const validUser = useMemo(
    () => inviteData.centizenNumber && inviteData.centizenNumber === profile.citizenIdentificationNumber,
    [inviteData.centizenNumber, profile.citizenIdentificationNumber]
  );

  useEffect(() => {
    if (activateCode) return;
    const fetchData = async () => {
      try {
        const payload = {
          langId: localStorage.getItem("i18nextLngId"),
        };
        const response = await getAccountList(payload);
        setListAccountCompany(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, [activateCode]);

  // Chuẩn hoá list companyId 1 lần
  const companyIds = useMemo(
    () =>
      (listAccountCompany ?? [])
        .map(it => Number(it?.companyId))
        .filter(Number.isFinite),
    [listAccountCompany]
  );

  const companyIdSet = useMemo(() => new Set(companyIds), [companyIds]);

  const isBelongCompany = useMemo(() => {
    const cid = Number(inviteData?.companyId);
    return Number.isFinite(cid) && companyIdSet.has(cid);
  }, [inviteData?.companyId, companyIdSet]);
  const url = window.location.href;
  const pathAndQuery = "/invitation" + new URL(url).search;

  const [isCancelled, setIsCancelled] = useState(false);

  useEffect(() => {
    if (activateCode) return;
    localStorage.setItem("redirectAfterLogin", pathAndQuery);
  }, [activateCode, pathAndQuery]);

  useEffect(() => {
    if (activateCode) return;

    const fetchInviteData = async () => {
      show();
      try {
        const { data } = await inviteDecode(encodeURIComponent(inviteCode));
        setInviteData(data);
      } catch (error) {
        if (isAxiosError(error)) {
          toast.error(t(error.response.data));
        }
      } finally {
        hide();
      }
    };

    if (inviteCode) {
      fetchInviteData();
    }
  }, [hide, show, t, inviteCode, activateCode]);

  useEffect(() => {
    if (!inviteData.inviteId) return;

    const fetchDetail = async () => {
      try {
        const res = await detailInviteEmail(inviteData.inviteId);
        const { responseCode, data } = res.data;

        if (responseCode === 1142) {
          // 👉 Lời mời đã bị hủy / không còn tồn tại
          setIsCancelled(true);
          setInviteDetailData(null);
        } else {
          setIsCancelled(false);
          setInviteDetailData(data?.[0]?.state);
        }
      } catch (err) {
        console.log("detailInviteEmail ERROR:", err);
      }
    };

    fetchDetail();
  }, [inviteData.inviteId]);


  useEffect(() => {
    if (validUser) {
      // if (isBelongCompany) {
      //   return navigate("/controlpanel/event-log-management");
      // }

      show();
      getCompany(inviteData.companyId, localStorage.getItem("i18nextLngId"))
        .then(({ data }) => {
          setCompany(data.data);
        })
        .catch((error) => {
          if (isAxiosError(error)) {
            toast.error(t(error.response.data));
          }
        })
        .finally(hide);
    } else {
      if (!isAuthenticated && inviteData.companyId && !inviteData.hasAccount) {
        navigate(`/sign-up?inviteCode=${encodeURIComponent(inviteCode)}`);
      }
    }
  }, [
    show,
    hide,
    t,
    inviteData,
    isAuthenticated,
    navigate,
    validUser,
    inviteCode,
    isBelongCompany,
  ]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    show();
    try {
      const response = await accessInviteEmail(
        inviteData.inviteId,
      );
      localStorage.removeItem("redirectAfterLogin");
      refreshProfile();
      toast.success(t("common.invite-user-successful") + company[0]?.companyName);
      if (isAdmin) {
        navigate("/controlpanel/dashboard");
      } else {
        navigate("/controlpanel/event-log-management");
      }
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      hide();
    }
  };

  const handleReject = async (event) => {
    event.preventDefault();
    show();
    try {
      const response = await rejectInviteEmail(
        inviteData.inviteId,
      );
      localStorage.removeItem("redirectAfterLogin");
      refreshProfile();
      toast.success(t("common.invite-user-rejected") + company[0]?.companyName);
      if (isAdmin) {
        navigate("/controlpanel/dashboard");
      } else {
        navigate("/controlpanel/event-log-management");
      }
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      hide();
    }

  };

  const handleReturnLogin = () => {
    localStorage.removeItem("redirectAfterLogin");
    if (isAdmin) {
      navigate("/controlpanel/dashboard");
    } else {
      navigate("/controlpanel/event-log-management");
    }
  }

  const getRoleLabel = (roleRaw) => {
    const keyByRole = {
      EMPLOYEE: "roles2.Employee",
      AUDITOR: "roles2.Auditor",
      ADMIN: "roles2.Admin",
      SUBCRIPTION: "roles2.SubscriptionAdmin", // người dùng gõ thiếu "s"
      SUBSCRIPTION: "roles2.SubscriptionAdmin", // phòng khi BE trả đúng chính tả
    };
    const key =
      keyByRole[roleRaw?.toString?.().toUpperCase?.()] || "roles2.Employee";
    return t(key);
  };

  const isReject = useMemo(() => {
    return inviteDetailData === 3;
  }, [inviteDetailData]);

  const messageTemplate = () => {
    if (isTokenExpires) {
      return <p className="font-medium">{t("common.content-021")}</p>;
    }

    if (!validUser) {
      return <p className="font-medium">{t("common.content-022")}</p>;
    }

    if (isBelongCompany) {
      return <p className="font-medium">{t("common.content-189")} {company[0]?.companyName}</p>;
    }

    if (isReject) {
      return <p className="font-medium">{t("common.content-192")} {company[0]?.companyName}</p>;
    }

    if (isCancelled) {
      return (
        <p className="font-medium">
          {t("common.invite-has-been-cancelled")}
        </p>
      );
    }

    return (
      <p className="font-medium">
        {inviteData.fromUser} {t("common.content-023")} {company[0]?.companyName}
      </p>
    );
  };

  useEffect(() => {
    if (isTokenExpires) {
      localStorage.removeItem("redirectAfterLogin");
    }
  }, [isTokenExpires]);

  // Đang redirect / logout để kích hoạt — full viewport, che mọi layout phía dưới đến khi sang SSO
  if (activateCode) {
    const isLoggingOut = activatePhase === "logging-out";

    return (
      <div className="fixed inset-0 z-[9999] min-h-screen flex flex-col items-center justify-center px-4 bg-white">
        <SpinnerRing
          loading
          size={48}
          color="#3B82F6"
        />
      </div>
    );
  }

  return (
    <div className="relative w-full order-1 lg:order-2 lg:pb-20 flex justify-center items-start pt-4 md:pt-8 min-h-[70vh] md:min-h-[45vh] custom-min-h">
      <div className="w-full max-w-5xl">
        {/* Logo + Language — giống Login */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <img
            src={logo_light}
            alt="CheckID BioSense"
            width={300}
            height={100}
          />
        </div>

        <div className="flex items-end justify-end gap-4 mb-6">
          <div className="relative">
            <button
              type="button"
              ref={anchorRefLanguage}
              onClick={() => setOpenLanguage((prev) => !prev)}
              className="flex items-center gap-2 px-3 py-2 border border-[#E5E7EB] rounded-xl bg-white hover:bg-gray-50"
              id="invitation-language-button"
              aria-controls={openLanguage ? "invitation-language-menu" : undefined}
              aria-expanded={openLanguage ? "true" : undefined}
              aria-haspopup="true"
            >
              <img
                src={currentFlag}
                alt={currentLang === "vi" ? "VI" : "EN"}
                className="w-[22px] h-[22px] rounded-full object-cover"
              />
              <ChevronDown10pxSvg
                className={`text-[#404040] transition-transform duration-300 ${openLanguage ? "rotate-180" : ""
                  }`}
              />
            </button>

            <Popper
              open={openLanguage}
              anchorEl={anchorRefLanguage.current}
              role={undefined}
              placement="bottom-end"
              transition
              disablePortal
              className="z-10"
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper
                    style={{
                      borderRadius: "8px",
                      marginTop: "8px",
                      minWidth: 120,
                    }}
                  >
                    <ClickAwayListener onClickAway={() => setOpenLanguage(false)}>
                      <NavbarLanguageMenuList
                        autoFocusItem={openLanguage}
                        sx={{
                          overflow: "hidden",
                          borderRadius: "8px",
                          ".MuiMenuItem-root": {
                            borderRadius: 0,
                            "&:hover": {
                              borderRadius: 0,
                            },
                            "&:first-of-type:hover": {
                              borderTopLeftRadius: "8px",
                              borderTopRightRadius: "8px",
                            },
                            "&:last-of-type:hover": {
                              borderBottomLeftRadius: "8px",
                              borderBottomRightRadius: "8px",
                            },
                          },
                        }}
                      >
                        <MenuItem onClick={() => handleChangeLanguage("en")}>
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <img
                              src={US}
                              alt="EN"
                              className="w-5 h-5 rounded-full object-cover shrink-0"
                            />
                            <span>{t("common.languageEn")}</span>
                          </div>
                        </MenuItem>
                        <MenuItem onClick={() => handleChangeLanguage("vi")}>
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <img
                              src={VN}
                              alt="VI"
                              className="w-5 h-5 rounded-full object-cover shrink-0"
                            />
                            <span>{t("common.languageVi")}</span>
                          </div>
                        </MenuItem>
                      </NavbarLanguageMenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </div>
        </div>

        <p className="font-extrabold text-[24px] text-[#1F2937]">
          {t("button.invite-new-user")}
        </p>
        <p className="my-5 font-medium text-sm">{messageTemplate()}</p>

        <div className="grid grid-cols-1 gap-y-6 w-full">
          <div>
            {validUser && !isTokenExpires && !isBelongCompany && !isReject && !isCancelled ? (
              <div className="flex flex-col gap-4">
                <PrimaryButtonFullWidth onClick={handleSubmit}>
                  {t("button.confirm")}
                </PrimaryButtonFullWidth>
                <WhiteButtonFullWidth onClick={handleReject}>
                  {t("button.reject")}
                </WhiteButtonFullWidth>
              </div>
            ) : (
              <Link to="/login" state={{ from: inviteCode }}>
                <PrimaryButtonFullWidth onClick={handleReturnLogin}>
                  {t("button.back-to-client")}
                </PrimaryButtonFullWidth>
              </Link>

              // <Link to='/login'>
              //    <PrimaryButtonFullWidth>{t('button.back-to-login')}</PrimaryButtonFullWidth>
              // </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invitation;
